import { useMemo, useState } from "react";

import { CheckCircle2, FileText, XCircle } from "lucide-react";

import type { BookingApprovalResponse } from "@api/generated/types.gen";

import { Badge } from "@components/ui/badge";
import { Button } from "@components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@components/ui/table";
import { Textarea } from "@components/ui/textarea";

import {
  useApproveBookingApproval,
  useRejectBookingApproval,
  useTriggerBookingSync,
} from "@hooks/mutations/use-booking-approvals";
import {
  type BookingApprovalsFilters,
  useBookingApprovals,
} from "@hooks/queries/use-booking-approvals";

import { FilterBar } from "./components/FilterBar";
import { useDebouncedValue } from "./hooks/use-debounced-value";
import { EMPTY_FILTERS, type RawFilters } from "./types";

const STATUS_FILTERS = ["PENDING", "APPROVED", "REJECTED", "ALL"] as const;
type StatusFilter = (typeof STATUS_FILTERS)[number];

const fmtDate = (v?: string | null): string =>
  v ? new Date(v).toLocaleString("en-AU", { dateStyle: "medium" }) : "—";

const formatBytes = (bytes?: number | null): string => {
  if (bytes == null) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const statusVariant = (
  status: string
): "default" | "secondary" | "destructive" | "outline" => {
  if (status === "APPROVED") return "default";
  if (status === "REJECTED") return "destructive";
  return "secondary";
};

const Field = ({ label, value }: { label: string; value?: string | null }) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-xs uppercase tracking-wide text-foreground/50">
      {label}
    </span>
    <span className="text-sm">{value?.toString().trim() || "—"}</span>
  </div>
);

const PAGE_SIZE = 20;

const BookingApprovalsPage = () => {
  const [filter, setFilter] = useState<StatusFilter>("PENDING");
  const [rawFilters, setRawFilters] = useState<RawFilters>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<BookingApprovalResponse | null>(null);
  const [rejectReason, setRejectReason] = useState("");

  const debouncedFilters = useDebouncedValue(rawFilters, 300);
  const appliedFilters: BookingApprovalsFilters = useMemo(
    () => ({
      bookingId: debouncedFilters.bookingId
        ? Number(debouncedFilters.bookingId)
        : undefined,
      medicalExaminer: debouncedFilters.medicalExaminer || undefined,
      referrer: debouncedFilters.referrer || undefined,
      examinee: debouncedFilters.examinee || undefined,
      appointmentDate: debouncedFilters.appointmentDate || undefined,
      secureAndSwitch:
        debouncedFilters.secureAndSwitch === "all"
          ? undefined
          : debouncedFilters.secureAndSwitch === "yes",
      matchStatus:
        debouncedFilters.matchStatus === "all"
          ? undefined
          : debouncedFilters.matchStatus,
      hasDocuments: debouncedFilters.withDocs ? true : undefined,
    }),
    [debouncedFilters]
  );

  const { data, isLoading } = useBookingApprovals({
    status: filter === "ALL" ? undefined : filter,
    filters: appliedFilters,
    page,
    limit: PAGE_SIZE,
  });
  const approvals = data?.items ?? [];
  const totalPages = Math.max(1, data?.total_pages ?? 1);

  // Any filter change invalidates the current page — reset to page 1 right
  // where the filter itself changes, so it lands before the (debounced)
  // query even re-runs.
  const onFilterChange = (s: StatusFilter) => {
    setFilter(s);
    setPage(1);
  };

  const onFiltersBarChange = (patch: Partial<RawFilters>) => {
    setRawFilters((f) => ({ ...f, ...patch }));
    setPage(1);
  };

  const approve = useApproveBookingApproval();
  const reject = useRejectBookingApproval();
  const sync = useTriggerBookingSync();

  const isPending = selected?.status === "PENDING";
  const approveError = approve.error instanceof Error ? approve.error.message : null;

  const closeDialog = () => {
    setSelected(null);
    setRejectReason("");
    approve.reset();
    reject.reset();
  };

  const onApprove = async () => {
    if (!selected) return;
    await approve
      .mutateAsync(selected.id)
      .then(closeDialog)
      .catch(() => {});
  };

  const onReject = async () => {
    if (!selected) return;
    await reject
      .mutateAsync({ id: selected.id, reason: rejectReason || undefined })
      .then(closeDialog)
      .catch(() => {});
  };

  const payload = useMemo(() => selected?.payload, [selected]);

  return (
    <div className="space-y-4 px-10 py-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Booking Approvals</h1>
          <p className="text-sm text-foreground/60">
            Review directory bookings and approve them into Report Studio cases.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => sync.mutate()}
          disabled={sync.isPending}
        >
          {sync.isPending ? "Syncing…" : "Sync now"}
        </Button>
      </div>

      <div className="flex gap-2">
        {STATUS_FILTERS.map((s) => (
          <Button
            key={s}
            size="sm"
            variant={filter === s ? "default" : "outline"}
            onClick={() => onFilterChange(s)}
          >
            {s.charAt(0) + s.slice(1).toLowerCase()}
          </Button>
        ))}
      </div>

      <FilterBar filters={rawFilters} onChange={onFiltersBarChange} />

      <div className="rounded-md border">
        <Table className="table-fixed">
          <TableHeader>
            <TableRow>
              <TableHead className="w-20 whitespace-normal break-words">
                Booking
              </TableHead>
              <TableHead className="w-36 whitespace-normal break-words">
                Medical Examiner
              </TableHead>
              <TableHead className="w-48 whitespace-normal break-words">
                Referrer
              </TableHead>
              <TableHead className="w-36 whitespace-normal break-words">
                Examinee
              </TableHead>
              <TableHead className="w-28 whitespace-normal break-words">
                Appointment
              </TableHead>
              <TableHead className="w-28 whitespace-normal break-words">
                Documents
              </TableHead>
              <TableHead className="w-32 whitespace-normal break-words">
                Secure&amp;Switch
              </TableHead>
              <TableHead className="w-36 whitespace-normal break-words">
                Match
              </TableHead>
              <TableHead className="w-24 whitespace-normal break-words">
                Status
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={9}
                  className="py-8 text-center text-foreground/50"
                >
                  Loading…
                </TableCell>
              </TableRow>
            ) : approvals.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={9}
                  className="py-8 text-center text-foreground/50"
                >
                  No booking approvals.
                </TableCell>
              </TableRow>
            ) : (
              approvals.map((a) => (
                <TableRow
                  key={a.id}
                  className="cursor-pointer"
                  onClick={() => {
                    approve.reset();
                    reject.reset();
                    setRejectReason("");
                    setSelected(a);
                  }}
                >
                  <TableCell className="whitespace-normal break-words font-medium">
                    {a.payload?.case_details?.case_number ?? a.vinci_booking_id}
                  </TableCell>
                  <TableCell className="whitespace-normal break-words">
                    {a.payload?.medical_examiner?.name || "—"}
                  </TableCell>
                  <TableCell className="whitespace-normal break-words">
                    {a.payload?.referrer?.organisation || "—"}
                  </TableCell>
                  <TableCell className="whitespace-normal break-words">
                    {a.payload?.examinee?.name || "—"}
                  </TableCell>
                  <TableCell className="whitespace-normal break-words">
                    {fmtDate(a.payload?.case_details?.appointment_date)}
                  </TableCell>
                  <TableCell className="whitespace-normal break-words">
                    {a.payload?.documents?.length ?? 0}
                  </TableCell>
                  <TableCell className="whitespace-normal break-words">
                    {a.payload?.case_details?.is_secure_and_switch ? (
                      <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400">
                        <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                        <span className="sr-only">Secure&amp;Switch: Yes</span>
                      </span>
                    ) : (
                      <span className="text-muted-foreground inline-flex items-center gap-1">
                        <XCircle className="h-4 w-4" aria-hidden="true" />
                        <span className="sr-only">Secure&amp;Switch: No</span>
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="whitespace-normal break-words">
                    <Badge
                      variant={
                        a.match_status === "UNMATCHED" ? "destructive" : "outline"
                      }
                    >
                      {a.match_status}
                    </Badge>
                  </TableCell>
                  <TableCell className="whitespace-normal break-words">
                    <Badge variant={statusVariant(a.status)}>{a.status}</Badge>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between">
        <p className="text-sm text-foreground/60">
          {data?.total ? `${data.total} total` : null}
        </p>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || isLoading}
          >
            Previous
          </Button>
          <span className="text-sm text-foreground/60">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages || isLoading}
          >
            Next
          </Button>
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              Booking{" "}
              {payload?.case_details?.case_number ?? selected?.vinci_booking_id}
            </DialogTitle>
          </DialogHeader>

          {payload && (
            <div className="min-w-0 space-y-5">
              <section className="grid grid-cols-2 gap-3">
                <Field
                  label="Medical Examiner"
                  value={payload.medical_examiner?.name}
                />
                <Field label="ME Email" value={payload.medical_examiner?.email} />
                <Field
                  label="Specialty"
                  value={payload.medical_examiner?.specialty}
                />
                <Field label="Match" value={`${selected?.match_status}`} />
              </section>
              <section className="grid grid-cols-2 gap-3">
                <Field label="Referrer" value={payload.referrer?.name} />
                <Field label="Organisation" value={payload.referrer?.organisation} />
                <Field label="Examinee" value={payload.examinee?.name} />
                <Field label="Employer" value={payload.examinee?.employer} />
              </section>
              <section className="grid grid-cols-2 gap-3">
                <Field
                  label="Claim Number"
                  value={payload.case_details?.claim_number}
                />
                <Field label="Case Type" value={payload.case_details?.case_type} />
                <Field
                  label="Service Type"
                  value={payload.case_details?.service_type}
                />
                <Field label="Location" value={payload.case_details?.location} />
                <Field
                  label="Appointment"
                  value={fmtDate(payload.case_details?.appointment_date)}
                />
                <Field
                  label="Booking Date"
                  value={fmtDate(payload.case_details?.booking_date)}
                />
                <Field
                  label="Date of Injury"
                  value={fmtDate(payload.case_details?.date_of_injury)}
                />
                <Field
                  label="Telehealth"
                  value={payload.case_details?.telehealth ? "Yes" : "No"}
                />
                <Field
                  label="Interpreter"
                  value={
                    payload.case_details?.interpreter_required
                      ? `Yes${
                          payload.case_details?.interpreter_language
                            ? ` (${payload.case_details.interpreter_language})`
                            : ""
                        }`
                      : "No"
                  }
                />
                <Field
                  label="Urgent Report"
                  value={payload.case_details?.urgent_report ? "Yes" : "No"}
                />
                <Field
                  label="Secure&Switch"
                  value={payload.case_details?.is_secure_and_switch ? "Yes" : "No"}
                />
              </section>

              <section className="space-y-2">
                <span className="text-xs uppercase tracking-wide text-foreground/50">
                  Documents ({payload.documents?.length ?? 0})
                </span>
                {payload.documents && payload.documents.length > 0 ? (
                  <ul className="divide-y divide-border rounded-md border">
                    {payload.documents.map((doc) => (
                      <li
                        key={doc.document_id ?? `${doc.file_name}-${doc.date_added}`}
                        className="flex flex-col gap-1 px-3 py-2 text-sm"
                      >
                        <div className="flex min-w-0 items-center gap-2">
                          <FileText className="h-4 w-4 shrink-0 text-foreground/50" />
                          <span className="truncate">
                            {doc.file_name || "Untitled"}
                          </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 pl-6 text-xs text-foreground/50">
                          {doc.file_ext && (
                            <Badge variant="outline" className="uppercase">
                              {doc.file_ext.replace(/^\./, "")}
                            </Badge>
                          )}
                          {doc.bytes != null && (
                            <span>{formatBytes(doc.bytes)}</span>
                          )}
                          <span>{fmtDate(doc.date_added)}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-foreground/50">No documents.</p>
                )}
              </section>

              {isPending && (
                <div className="space-y-2">
                  <Textarea
                    placeholder="Reason for rejection (optional)"
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                  />
                  {approveError && (
                    <p className="text-sm text-destructive">{approveError}</p>
                  )}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {isPending ? (
              <>
                <Button
                  variant="outline"
                  onClick={onReject}
                  disabled={reject.isPending || approve.isPending}
                >
                  {reject.isPending ? "Rejecting…" : "Reject"}
                </Button>
                <Button
                  onClick={onApprove}
                  disabled={approve.isPending || reject.isPending}
                >
                  {approve.isPending ? "Approving…" : "Approve"}
                </Button>
              </>
            ) : (
              <Button variant="outline" onClick={closeDialog}>
                Close
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BookingApprovalsPage;
